import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormLabel,
  IconButton, InputAdornment, MenuItem, Radio, RadioGroup, Stack, TextField,
  Tooltip, Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  EditOutlined as EditIcon,
  Add as AddIcon,
  Send as SendIcon,
  CancelScheduleSend as CancelScheduleSendIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  UploadFile as UploadFileIcon,
  Search as SearchIcon,
  PeopleAltOutlined as PeopleIcon,
  MarkEmailUnreadOutlined as PendingIcon,
  HistoryOutlined as FormerIcon,
} from '@mui/icons-material';
import apiClient from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { formatPhoneNumber } from '../../utils/phoneUtils';
import PageHeader from '../../components/ui/PageHeader';
import PageBody from '../../components/ui/PageBody';
import DataTable, { Column } from '../../components/ui/DataTable';
import ToneChip from '../../components/ui/ToneChip';
import { EmptyState } from '../../components/ui/States';
import { NavPanel, PanelButton, PanelGroup } from '../../components/layout/NavPanel';
import { status as statusTokens } from '../../theme';
import BulkInviteDialog from './BulkInviteDialog';
import EditUserDialog from './EditUserDialog';

interface User {
  _id: string;
  userId: string;
  firstName: string;
  lastName: string;
  name?: string;
  email: string;
  phone: string;
  role: string;
  /** This person also holds the companyless platform (SA) account. */
  alsoPlatformAdmin?: boolean;
  status: string;
  companyId: string;
  /** From the pay profile; present for AO/CA callers only. */
  fedexId?: string;
}

const UserList: React.FC = () => {
  const { account } = useAuthStore();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);
  const [companies, setCompanies] = useState<{ _id: string; name: string }[]>([]);
  const [targetCompanyId, setTargetCompanyId] = useState('');
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  // 'all' | 'pending' | `role:${string}`
  const [segment, setSegment] = useState('all');

  // Everything below `password` is optional — an admin onboarding a crew in
  // one sitting fills in the top four fields and sends.
  const EMPTY_NEW_USER = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'Driver',
    activationMethod: 'email' as 'email' | 'password',
    password: '',
    employeeId: '',
    managerId: '',
    address: '',
    notes: '',
    licenseExpiryDate: '',
    dotCardExpiryDate: '',
    mecExpirationDate: '',
  };

  const [newUser, setNewUser] = useState(EMPTY_NEW_USER);
  const [showOptionalFields, setShowOptionalFields] = useState(false);

  const fetchUsers = useCallback(async (bustCache = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use /visible endpoint to get all users in current company (no pagination limit)
      // Add timestamp to bust cache when needed (after creating/updating users)
      const cacheBuster = bustCache ? `&_t=${Date.now()}` : '';
      const data: any = await apiClient.get(`/api/user/visible?limit=1000${cacheBuster}`);
      
      
      // API returns accounts with nested userId, transform to flat user objects
      const accounts = data.users || [];
      const transformedUsers = accounts.map((account: any) => ({
        _id: account._id,
        userId: account.userId?._id || account.userId,
        firstName: account.userId?.firstName || '',
        lastName: account.userId?.lastName || '',
        name: `${account.userId?.firstName || ''} ${account.userId?.lastName || ''}`.trim(),
        email: account.userId?.email || '',
        phone: account.userId?.phone || '',
        role: account.role,
        alsoPlatformAdmin: !!account.alsoPlatformAdmin,
        status: account.status,
        companyId: account.companyId?._id || account.companyId,
        fedexId: account.fedexId || '',
      }));
      
      // Outstanding invitations first — they are the rows that need an action
      // (resend or remove); the server already orders each group newest-first.
      const STATUS_ORDER: Record<string, number> = { pending: 0, active: 1, suspended: 2, former: 3 };
      transformedUsers.sort(
        (a: User, b: User) => (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3)
      );

      setUsers(transformedUsers);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
    // No dependency on `account`: the request is scoped by the bearer token,
    // and switching companies reloads the page (see Topbar), so there is no
    // in-place account change this needs to react to.
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // An SA account belongs to no company, so `account.companyId` has
  // nothing to offer the invite — the server requires an explicit target
  // ("companyId is required when a system admin creates a user"). Load
  // the companies once the dialog opens so the SA can point at one.
  useEffect(() => {
    if (!addDialogOpen || account?.role !== 'SA' || companies.length) return;
    apiClient.get('/api/companies')
      .then((data: any) => setCompanies(data.companies || []))
      .catch(() => setCompanies([]));
  }, [addDialogOpen, account?.role, companies.length]);

  const handleAddUser = async () => {
    if (!newUser.firstName || !newUser.lastName || !newUser.email || !newUser.phone) {
      setError('Name, email and phone are required.');
      return;
    }

    // A new SA is companyless by definition, so only company roles need a
    // destination named.
    if (account?.role === 'SA' && newUser.role !== 'SA' && !targetCompanyId) {
      setError('Pick the company this person is joining.');
      return;
    }

    if (newUser.activationMethod === 'password' && !newUser.password) {
      setError('Password is required when using password activation');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      const userData: any = {
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        email: newUser.email,
        phone: newUser.phone,
        role: newUser.role,
        // An SA names the company; everybody else can only mean their own.
        companyId: account?.role === 'SA' ? targetCompanyId : account?.companyId,
      };

      // If password activation, include password (backend will set account status to active)
      if (newUser.activationMethod === 'password') {
        userData.password = newUser.password;
      }
      // Otherwise, email activation will be sent (account status: pending)

      // Optional profile fields — only sent when actually filled in, so an
      // untouched field never overwrites anything with an empty string.
      (['employeeId', 'managerId', 'address', 'notes',
        'licenseExpiryDate', 'dotCardExpiryDate', 'mecExpirationDate'] as const)
        .forEach((field) => {
          const value = newUser[field]?.trim?.() ?? newUser[field];
          if (value) userData[field] = value;
        });

      const response: any = await apiClient.post('/api/user', userData);
      
      // Check if this was an existing user being added to the company
      const isExistingUser = response?.user?.isExistingUser || false;
      
      let successMsg;
      if (isExistingUser) {
        // Existing user invited to join this company
        successMsg = `${response?.user?.firstName} ${response?.user?.lastName} has been invited to join your company! They'll receive an email to accept the invitation and can use their existing TopoSDS credentials.`;
      } else if (newUser.activationMethod === 'email') {
        // New user with email activation
        successMsg = 'User added successfully! Activation email sent.';
      } else {
        // New user with password (immediate activation)
        successMsg = 'User added successfully and activated!';
      }
      
      setSuccess(successMsg);
      setAddDialogOpen(false);
      setNewUser(EMPTY_NEW_USER);
      setShowOptionalFields(false);

      await fetchUsers(true); // Bust cache to get fresh data
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      // Handle specific error for duplicate account
      if (err.response?.data?.error === 'ACCOUNT_EXISTS') {
        setError(`${newUser.firstName} ${newUser.lastName} (${newUser.email}) already has an account in your company.`);
      } else {
        setError(err.response?.data?.message || 'Failed to add user');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendInvitation = async (user: User) => {
    try {
      setError(null);
      await apiClient.post('/api/invitations/resend', { userId: user.userId });
      setSuccess(`Invitation resent to ${user.firstName} ${user.lastName}.`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to resend invitation');
    }
  };

  const handleRevokeInvitation = async (user: User) => {
    const label = `${user.firstName} ${user.lastName}${user.email ? ` (${user.email})` : ''}`;
    if (!window.confirm(`Revoke the invitation to ${label}? The link in that email will stop working.`)) {
      return;
    }
    try {
      setError(null);
      await apiClient.post('/api/invitations/revoke', { accountId: user._id });
      setSuccess(`Invitation to ${label} revoked.`);
      await fetchUsers(true);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to revoke invitation');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setSubmitting(true);
      setError(null);
      await apiClient.delete(`/api/user/${userToDelete._id}`);
      setSuccess(`User ${userToDelete.firstName} ${userToDelete.lastName} deleted successfully! They have been removed from all chats.`);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
      console.error('Error deleting user:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------- segments
  //
  // The list is filtered from the sidebar panel rather than from controls
  // above the table, matching Operations and Files. Pending invitations get
  // their own segment because they are the only rows here that carry an
  // action someone has to take.
  const pendingCount = users.filter((u) => u.status === 'pending').length;
  // Former members (ex-drivers kept for payroll and records) have a segment
  // of their own and stay out of everyone else's.
  const current = users.filter((u) => u.status !== 'former');
  const formerCount = users.length - current.length;

  const roleCounts = current.reduce<Record<string, number>>((acc, u) => {
    acc[u.role] = (acc[u.role] || 0) + 1;
    return acc;
  }, {});

  const visible = users.filter((u) => {
    if (segment === 'former' ? u.status !== 'former' : u.status === 'former') return false;
    if (segment === 'pending' && u.status !== 'pending') return false;
    if (segment.startsWith('role:') && u.role !== segment.slice(5)) return false;
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return [u.name, u.firstName, u.lastName, u.email, u.phone, u.fedexId]
      .some((v) => (v || '').toLowerCase().includes(term));
  });

  const columns: Column<User>[] = [
    {
      key: 'name',
      header: 'Name',
      width: 220,
      render: (u) => (
        <Typography variant="body2" fontWeight={600}>
          {u.name || `${u.firstName} ${u.lastName}`}
        </Typography>
      ),
    },
    {
      key: 'email',
      hide: 'tablet',
      header: 'Email',
      render: (u) => <Typography variant="body2" color="text.secondary">{u.email}</Typography>,
    },
    {
      key: 'phone',
      hide: 'phone',
      header: 'Phone',
      render: (u) => (
        <Typography variant="body2" color="text.secondary">
          {formatPhoneNumber(u.phone) || '—'}
        </Typography>
      ),
    },
    ...(users.some((u) => u.fedexId) ? [{
      key: 'fedexId',
      header: 'FedEx ID',
      hide: 'tablet',
      render: (u: User) => <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>{u.fedexId || '—'}</Typography>,
    } as Column<User>] : []),
    {
      key: 'role',
      header: 'Role',
      // Two hats, two tags. The first chip is this person's role IN THIS
      // COMPANY — the one every action here runs as. The second appears
      // when the same person also holds the platform (SA) account, so the
      // directory does not present the platform admin as just another CA.
      render: (u) => (
        <Stack direction="row" gap={0.5}>
          <Chip label={u.role} size="small" variant="outlined" />
          {u.alsoPlatformAdmin && u.role !== 'SA' && (
            <Tooltip title="Also the platform administrator">
              <Chip label="SA" size="small" color="primary" variant="outlined" />
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (u) => (
        <ToneChip
          tone={u.status === 'active' ? 'ok' : u.status === 'pending' ? 'warn' : 'neutral'}
          label={u.status}
          hollow={u.status === 'pending' || u.status === 'former'}
        />
      ),
    },
    {
      key: 'actions',
      hide: 'phone',
      header: '',
      align: 'right',
      width: 120,
      render: (u) => (
        <Stack direction="row" justifyContent="flex-end" onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => setUserToEdit(u)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {u.status === 'pending' ? (
            <>
              <Tooltip title="Resend invitation">
                <IconButton size="small" onClick={() => handleResendInvitation(u)}>
                  <SendIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Revoke invitation">
                <IconButton size="small" onClick={() => handleRevokeInvitation(u)}>
                  <CancelScheduleSendIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Remove from company">
              <IconButton
                size="small"
                onClick={() => { setUserToDelete(u); setDeleteDialogOpen(true); }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <>
      <NavPanel>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          sx={{ mb: 2.5, py: 1 }}
        >
          Add a user
        </Button>

        <PanelGroup label="People">
          <PanelButton
            icon={<PeopleIcon />}
            label="Everyone"
            count={current.length}
            active={segment === 'all'}
            onClick={() => setSegment('all')}
          />
          <PanelButton
            icon={<PendingIcon />}
            label="Pending invites"
            count={pendingCount}
            active={segment === 'pending'}
            onClick={() => setSegment('pending')}
          />
          {formerCount > 0 && (
            <PanelButton
              icon={<FormerIcon />}
              label="Former"
              count={formerCount}
              active={segment === 'former'}
              onClick={() => setSegment('former')}
            />
          )}
        </PanelGroup>

        {Object.keys(roleCounts).length > 1 && (
          <PanelGroup label="By role">
            {Object.entries(roleCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([role, count]) => (
                <PanelButton
                  key={role}
                  label={role}
                  count={count}
                  active={segment === `role:${role}`}
                  onClick={() => setSegment(`role:${role}`)}
                />
              ))}
          </PanelGroup>
        )}
      </NavPanel>

      <PageBody>
        <PageHeader
          title="Users"
          subtitle={
            pendingCount > 0
              ? `${users.length} in this company · ${pendingCount} invitation${pendingCount === 1 ? '' : 's'} awaiting acceptance`
              : `${users.length} in this company`
          }
          // "Add a user" is the panel's primary button, the same place
          // Files keeps "File a document". Repeating it in the header put
          // the same action on screen twice, six inches apart.
          actions={
            <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => setBulkDialogOpen(true)}>
              Import CSV
            </Button>
          }
        >
          <TextField
            placeholder="Find someone by name, email or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ maxWidth: 420, width: '100%' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
        </PageHeader>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>
        )}

        <DataTable
          columns={columns}
          rows={visible}
          rowKey={(u) => u._id}
          loading={loading}
          onRowClick={(u) => navigate(`/users/${u._id}`)}
          rowAccent={(u) => (u.status === 'pending' ? statusTokens.warn : undefined)}
          empty={
            <EmptyState
              compact
              icon={<PeopleIcon />}
              title={
                search || segment !== 'all'
                  ? 'Nobody matches that'
                  : 'No one here yet'
              }
              description={
                search || segment !== 'all'
                  ? 'Try a different search, or switch back to Everyone in the sidebar.'
                  : 'Add your first user, or import a crew from a CSV.'
              }
              action={
                search || segment !== 'all'
                  ? { label: 'Show everyone', onClick: () => { setSearch(''); setSegment('all'); } }
                  : { label: 'Add a user', onClick: () => setAddDialogOpen(true), icon: <AddIcon /> }
              }
            />
          }
        />
      </PageBody>

      <EditUserDialog
        open={!!userToEdit}
        user={userToEdit}
        callerRole={account?.role || ''}
        onClose={() => setUserToEdit(null)}
        onSaved={(message) => { setSuccess(message); fetchUsers(true); }}
      />

      {/* Add User Dialog */}
      <BulkInviteDialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        onImported={() => fetchUsers(true)}
      />

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="First Name"
              value={newUser.firstName}
              onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              value={newUser.lastName}
              onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Phone"
              value={newUser.phone}
              onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
              fullWidth
              required
            />
            {account?.role === 'SA' && newUser.role !== 'SA' && (
              <TextField
                label="Company"
                select
                value={targetCompanyId}
                onChange={(e) => setTargetCompanyId(e.target.value)}
                fullWidth
                required
                helperText="A platform account belongs to no company, so say which one this person is joining."
              >
                {companies.map((c) => (
                  <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Role"
              select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              fullWidth
              required
            >
              {/* Offer what the server will accept from this caller: only
                  an SA mints an SA (ROLE_ESCALATION_FORBIDDEN), and a BC
                  adds Drivers only (BC_DRIVERS_ONLY). */}
              {account?.role === 'SA' && <MenuItem value="SA">SA</MenuItem>}
              {account?.role !== 'BC' && <MenuItem value="CA">CA</MenuItem>}
              {account?.role !== 'BC' && <MenuItem value="AO">AO</MenuItem>}
              {account?.role !== 'BC' && <MenuItem value="BC">BC</MenuItem>}
              <MenuItem value="Driver">Driver</MenuItem>
              {account?.role !== 'BC' && <MenuItem value="Maintainer">Maintainer</MenuItem>}
            </TextField>

            {/* Activation Method */}
            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormLabel component="legend">Activation Method</FormLabel>
              <RadioGroup
                value={newUser.activationMethod}
                onChange={(e) => setNewUser({ ...newUser, activationMethod: e.target.value as 'email' | 'password', password: '' })}
              >
                <FormControlLabel 
                  value="email" 
                  control={<Radio />} 
                  label="Send activation email (user sets password)" 
                />
                <FormControlLabel 
                  value="password" 
                  control={<Radio />} 
                  label="Set password now (activate immediately)" 
                />
              </RadioGroup>
            </FormControl>

            {/* Password field - only show if password activation selected */}
            {newUser.activationMethod === 'password' && (
              <TextField
                label="Password"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                fullWidth
                required
                helperText="User will be activated immediately with this password"
              />
            )}

            {/* Everything below is optional and collapsed by default, so
                onboarding a crew stays a four-field job. */}
            <Divider sx={{ mt: 1 }} />
            <Button
              onClick={() => setShowOptionalFields((s) => !s)}
              endIcon={showOptionalFields ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ alignSelf: 'flex-start' }}
              size="small"
            >
              {showOptionalFields ? 'Hide additional details' : 'Add employment details (optional)'}
            </Button>

            <Collapse in={showOptionalFields} unmountOnExit>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <TextField
                  label="Employee ID"
                  value={newUser.employeeId}
                  onChange={(e) => setNewUser({ ...newUser, employeeId: e.target.value })}
                  fullWidth
                />

                <TextField
                  label="Manager"
                  select
                  value={newUser.managerId}
                  onChange={(e) => setNewUser({ ...newUser, managerId: e.target.value })}
                  fullWidth
                  helperText="Who this person reports to"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {users
                    .filter((u) => u.status === 'active')
                    .map((u) => (
                      <MenuItem key={u._id} value={u._id}>
                        {u.name || `${u.firstName} ${u.lastName}`} — {u.role}
                      </MenuItem>
                    ))}
                </TextField>

                <TextField
                  label="Address"
                  value={newUser.address}
                  onChange={(e) => setNewUser({ ...newUser, address: e.target.value })}
                  fullWidth
                  multiline
                  minRows={2}
                />

                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  Compliance expiry dates
                </Typography>
                <TextField
                  label="Driver's license expires"
                  type="date"
                  value={newUser.licenseExpiryDate}
                  onChange={(e) => setNewUser({ ...newUser, licenseExpiryDate: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="DOT card expires"
                  type="date"
                  value={newUser.dotCardExpiryDate}
                  onChange={(e) => setNewUser({ ...newUser, dotCardExpiryDate: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Medical certificate expires"
                  type="date"
                  value={newUser.mecExpirationDate}
                  onChange={(e) => setNewUser({ ...newUser, mecExpirationDate: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label="Notes"
                  value={newUser.notes}
                  onChange={(e) => setNewUser({ ...newUser, notes: e.target.value })}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Box>
            </Collapse>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleAddUser}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>
              {userToDelete?.firstName} {userToDelete?.lastName}
            </strong>
            ?
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            This will automatically remove them from all chats and log system messages.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteUser}
            color="error"
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserList;
